import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, Switch } from "antd";
// eslint-disable-next-line import/no-extraneous-dependencies
import classNames from "classnames";
import {
  Control,
  Controller,
  UseFormSetValue,
  useFormState,
  useWatch,
} from "react-hook-form";
import {
  SectionThirdFormType,
  SectionThreeLocationType,
  sectionThreeLocationTypeValues,
} from "../../../../model/types";
import "./general-info-place.scss";

interface GeneralInfoPlaceProps {
  control: Control<SectionThirdFormType>;
  setValue: UseFormSetValue<SectionThirdFormType>;
  initialLocationType?: string;
  disabled?: boolean;
}

const locationTypeMap = {
  axes: "AXES",
  rows: "RANKS",
  marks: "MARKS",
  floors: "FLOORS",
  tiers: "TIERS",
  sections: "SECTIONS",
  rooms: "PREMISES",
} as const satisfies Record<string, SectionThreeLocationType>;

type LocationFieldKey = keyof typeof locationTypeMap;

const LOCATION_DEFAULT: SectionThreeLocationType = "LOCATION";

const isKnownLocationType = (
  value?: string,
): value is SectionThreeLocationType =>
  !!value &&
  (sectionThreeLocationTypeValues as readonly string[]).includes(value);

export const GeneralInfoPlace = ({
  control,
  setValue,
  initialLocationType,
  disabled = false,
}: GeneralInfoPlaceProps) => {
  const { errors } = useFormState({
    control,
    name: ["locationDescription"],
  });

  const locationDescription = useWatch({
    control,
    name: "locationDescription",
  });

  const isDetailedInitial = useMemo(() => {
    return (
      isKnownLocationType(initialLocationType) &&
      initialLocationType !== LOCATION_DEFAULT
    );
  }, [initialLocationType]);

  const [isDetailed, setIsDetailed] = useState(isDetailedInitial);

  const initialActiveField = useMemo(() => {
    const match = Object.entries(locationTypeMap).find(
      ([, value]) => value === initialLocationType,
    );
    return (match?.[0] as LocationFieldKey | undefined) ?? null;
  }, [initialLocationType]);

  const [activeField, setActiveField] = useState<LocationFieldKey | null>(
    initialActiveField,
  );

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    const isDetailedNow =
      isKnownLocationType(initialLocationType) &&
      initialLocationType !== LOCATION_DEFAULT;

    setIsDetailed(isDetailedNow);

    const match = Object.entries(locationTypeMap).find(
      ([, value]) => value === initialLocationType,
    );
    const field = (match?.[0] as LocationFieldKey | undefined) ?? null;
    setActiveField(field);

    if (match) {
      setValue("locationType", match[1]);
    } else {
      setValue("locationType", LOCATION_DEFAULT);
    }
  }, [initialLocationType, setValue]);

  useEffect(() => {
    const currentDesc = locationDescription?.toLowerCase?.() || "";
    const match = Object.entries(locationTypeMap).find(([key]) =>
      currentDesc.includes(key),
    );

    if (isDetailed) {
      if (match) {
        setActiveField(match[0] as LocationFieldKey);
        setValue("locationType", match[1]);
      } else {
        setActiveField(null);
        setValue("locationType", LOCATION_DEFAULT);
      }
    } else {
      setActiveField(null);
      setValue("locationType", LOCATION_DEFAULT);
    }
  }, []);

  useEffect(() => {
    if (isDetailed && activeField && inputRefs.current[activeField]) {
      setTimeout(() => {
        inputRefs.current[activeField]?.focus();
      }, 0);
    }
  }, [isDetailed, activeField]);

  const handleInputChange = (name: LocationFieldKey, value: string) => {
    if (value && name !== activeField) {
      setActiveField(name);
      setValue("locationType", locationTypeMap[name]);
    } else if (!value && name === activeField) {
      setActiveField(null);
      setValue("locationType", LOCATION_DEFAULT);
    }
  };

  const handleSwitchChange = (checked: boolean) => {
    setIsDetailed(checked);
    setValue("locationDescription", "");
    if (!checked) {
      setValue("locationType", LOCATION_DEFAULT);
    }
    setActiveField(null);
  };

  return (
    <Form.Item style={{ marginBottom: 24 }}>
      <div className="general-info-place__header">
        <div>Местоположение</div>
        <div className="general-info-place__switch">
          <span>Подробно</span>
          <Switch checked={isDetailed} onChange={handleSwitchChange} disabled={disabled} />
        </div>
      </div>

      {!isDetailed && (
        <Controller
          name="locationDescription"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <Input.TextArea
              {...field}
              autoSize
              placeholder=""
              disabled={disabled}
              maxLength={255}
              showCount
              onChange={(e) => field.onChange(e.target.value.slice(0, 255))}
            />
          )}
        />
      )}

      <div
        className={classNames("general-info-place__details", {
          show: isDetailed,
        })}
      >
        <div className="general-info-place__grid">
          {(Object.keys(locationTypeMap) as LocationFieldKey[]).map((name) => (
            <Form.Item
              label={
                {
                  axes: "Оси",
                  rows: "Ряды",
                  marks: "Отметки",
                  floors: "Этажи",
                  tiers: "Ярусы",
                  sections: "Секции",
                  rooms: "Помещения",
                }[name]
              }
              key={name}
            >
              <Controller
                name="locationDescription"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder=""
                    disabled={disabled || (activeField !== null && activeField !== name)}
                    value={activeField === name ? field.value : ""}
                    maxLength={255}
                    ref={(el) => {
                      inputRefs.current[name] =
                        el as unknown as HTMLInputElement;
                    }}
                    onFocus={() => {
                      setActiveField(name);
                      setValue("locationType", locationTypeMap[name]);
                    }}
                    onChange={(e) => {
                      const truncated = e.target.value.slice(0, 255);
                      field.onChange({ ...e, target: { ...e.target, value: truncated } });
                      handleInputChange(name, truncated);
                    }}
                  />
                )}
              />
            </Form.Item>
          ))}
        </div>
        <div className="general-info-place__note">
          Одно из полей обязательно для заполнения
        </div>
      </div>

      <Controller
        name="locationType"
        control={control}
        render={({ field }) => <input type="hidden" {...field} />}
      />

      {errors.locationDescription && (
        <div
          className="general-info-place__error"
        >
          {errors.locationDescription?.message?.toString()}
        </div>
      )}
    </Form.Item>
  );
};
